import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Table, Form, Input, Button } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { ColumnsType } from 'antd/es/table';
import { cloneDeep } from 'lodash';
import intl from 'react-intl-universal';

import { AMOUNT_REG } from '@/config';
import { validateAmount, formatAmount } from '@/pages/bill/util/helper';
import { isShowCalculator } from '@/components/RefoundCalculatorModal/help';
import { parse2Number, parseFen2Yuan, parseYuan2Fen } from '@/utils/number';
import IntlContext from '@/components/Intl/IntlContext';

import { EditableTableProps, EditableRowProps, EditableCellProps } from './interface';
import styles from './index.module.scss';

const EditableContext = createContext<FormInstance<any> | null>(null);

const EditableRow: React.FC<EditableRowProps> = ({ ...restProps }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...restProps} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell: React.FC<EditableCellProps> = ({
  editable,
  inputRules,
  isPriceData,
  maxValue,
  dataIndex,
  record,
  handleSave,
  handleOpenCalculator,
  children,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const intlCtx = useContext(IntlContext);
  // context form
  const form = useContext(EditableContext);
  const { setFieldsValue, validateFields } = form;

  const toggleEdit = () => {
    setEditing(!editing);
  };

  const saveInput = async (e) => {
    // format input
    const max = maxValue ? record[maxValue] : null;
    const result = formatAmount(e.target.value, {
      max,
    });
    setFieldsValue({ [dataIndex]: result });

    setTimeout(() => {
      const obj = {
        ...record,
      };
      validateFields()
        .then(() => {
          const value = isPriceData ? parse2Number(parseYuan2Fen(+result)) : result;
          obj[dataIndex as string] = value;
          Object.assign(obj, { amountInvalid: false });
          // callback save
          handleSave({
            ...obj,
          });
          toggleEdit();
        })
        .catch((err) => {
          const invalidValue = err?.values[dataIndex];
          setFieldsValue({ [dataIndex]: invalidValue });
          Object.assign(obj, { amountInvalid: true, invalidValue });
          // callback save
          handleSave({
            ...obj,
          });
        });
    }, 0);
  };

  // 金额处理
  const dealAmount = (isAmount, source) => {
    let r;
    if (isAmount && AMOUNT_REG.test(source as string)) {
      r = parse2Number(parseFen2Yuan(source));
    } else {
      r = record[dataIndex];
    }
    return r;
  };

  // 最大值
  const setMaxValue = async () => {
    const max = parseFen2Yuan(record[maxValue]);
    setFieldsValue({ [dataIndex]: max });
    const obj = { ...record };
    obj[dataIndex as string] = record[maxValue];
    // callback save
    handleSave({ ...obj });
  };

  // input auto focus when editing
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
      // set default
      const val = dealAmount(isPriceData, record[dataIndex]);
      setFieldsValue({ [dataIndex]: val });
    }
  }, [editing]);

  let childNode = children;

  if (editable) {
    // deal with amount value
    const initValue = dealAmount(isPriceData, record[dataIndex]);
    const defaultRules = isPriceData
      ? [
          {
            validator: (_, value) => {
              return validateAmount(value, {
                required: true,
              });
            },
          },
        ]
      : [];

    const rules = inputRules ? [].concat(inputRules, defaultRules) : defaultRules;

    childNode = editing ? (
      <Form.Item
        className={styles['editable-cell-form-item']}
        name={dataIndex}
        rules={rules}
        initialValue={initValue}
      >
        <Input
          ref={inputRef}
          prefix={isPriceData ? intlCtx.TenantBaseConfig.CurrencySymbol : null}
          maxLength={isPriceData ? 9 : null}
          onPressEnter={saveInput}
          onBlur={saveInput}
        />
      </Form.Item>
    ) : (
      <div className={styles['editable-cell-value-wrap']} onClick={toggleEdit}>
        {children}
      </div>
    );
  }

  const openCalculator = () => {
    const max = parseFen2Yuan(record[maxValue]);
    setFieldsValue({ [dataIndex]: max });
    const obj = { ...record };
    obj[dataIndex as string] = record[maxValue];
    // callback save
    handleOpenCalculator({ ...obj });
  };

  return (
    <td key={dataIndex} {...restProps}>
      <div className={maxValue ? styles['editable-cell-max'] : ''}>
        {childNode}
        {maxValue ? (
          <Button style={{ paddingRight: 0 }} type="link" onClick={() => setMaxValue()}>
            {intl.get('cmp_editable_table_max_btn')}
          </Button>
        ) : (
          ''
        )}
        {maxValue && isShowCalculator(record?.subjectCode) ? (
          <span className={styles.refoundCalculator} onClick={() => openCalculator()} />
        ) : (
          ''
        )}
      </div>
    </td>
  );
};

const EditableTable: React.FC<EditableTableProps> = (props) => {
  const { columns, dataSource, rowKey, ...restProps } = props;

  const dataList = useMemo(() => {
    return dataSource;
  }, [dataSource]);

  const handleSave = (row) => {
    const newData = cloneDeep(dataList);
    const curIndex = newData.findIndex((item) => row[`${rowKey}`] === item[`${rowKey}`]);
    const item = newData[curIndex];
    // replace midified row
    newData.splice(curIndex, 1, {
      ...item,
      ...row,
    });

    // callback
    props.saveDataList(newData, row);
  };

  const handleOpenCalculator = (row) => {
    const newData = cloneDeep(dataList);
    const curIndex = newData.findIndex((item) => row[`${rowKey}`] === item[`${rowKey}`]);
    const item = newData[curIndex];
    // replace midified row
    newData.splice(curIndex, 1, {
      ...item,
      ...row,
    });

    // callback
    props.openCalculator(newData, row);
  };

  const tableColumns: ColumnsType<any> = columns.map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        title: col.title,
        editable: col.editable,
        isPriceData: col.isPriceData,
        maxValue: col.maxValue,
        dataIndex: col.dataIndex,
        inputRules: col.inputRules,
        handleSave,
        handleOpenCalculator,
      }),
    };
  });

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };
  return (
    <Table
      bordered
      rowKey={rowKey}
      components={components}
      rowClassName={() => styles['editable-row']}
      columns={tableColumns}
      dataSource={dataList}
      {...restProps}
    />
  );
};

export default EditableTable;
